import classNames from 'classnames';
import { node, string } from 'prop-types';
import React from 'react';
import { Field } from 'react-final-form';

import css from './FieldRadioButton.module.css';

const IconRadioButton = props => {
  const { checkedClassName } = props;
  return (
    <div>
      <svg className={props.className} width="14" height="14" xmlns="http://www.w3.org/2000/svg">
        <circle
          className={props.showAsRequired ? css.required : css.notChecked}
          cx="5"
          cy="19"
          r="6"
          transform="translate(2 -12)"
          strokeWidth="2"
          fill="none"
          fillRule="evenodd"
        />

        <g
          className={classNames(css.checked, checkedClassName || css.checkedStyle)}
          transform="translate(2 -12)"
          fill="none"
          fillRule="evenodd"
        >
          <circle strokeWidth="2" cx="5" cy="19" r="6" />
          <circle fill="#FFF" fillRule="nonzero" cx="5" cy="19" r="3" />
        </g>
      </svg>
    </div>
  );
};

IconRadioButton.defaultProps = { className: null };

IconRadioButton.propTypes = { className: string };

const FieldRadioButtonComponent = props => {
  const {
    rootClassName,
    className,
    svgClassName,
    checkedClassName,
    hideRadioIndicator,
    id,
    label,
    subLabel,
    showAsRequired,
    variant = 'default',
    children,
    ...rest
  } = props;

  const classes = classNames(
    rootClassName || css.root,
    className,
    variant === 'card' && css.cardVariant
  );

  const radioButtonProps = {
    id,
    className: css.input,
    component: 'input',
    type: 'radio',
    tabIndex: 0,
    ...rest,
  };

  const handleKeyDown = e => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      const input = document.getElementById(id);
      if (input) {
        input.click();
      }
    }
  };

  if (variant === 'card') {
    return (
      <span className={classes}>
        <Field {...radioButtonProps} />
        <label htmlFor={id} className={css.cardLabel} aria-labelledby={`${id}-label`}>
          <div className={css.cardContent}>
            <h3 id={`${id}-label`} className={css.cardTitle}>
              {label}
            </h3>
            {subLabel && <p className={css.cardDescription}>{subLabel}</p>}
            {children && <div className={css.cardSlot}>{children}</div>}
          </div>
          {!hideRadioIndicator && (
            <div className={css.radioIndicator}>
              <IconRadioButton
                className={svgClassName}
                checkedClassName={checkedClassName}
                showAsRequired={showAsRequired}
              />
            </div>
          )}
        </label>
      </span>
    );
  }

  return (
    <span className={classes}>
      <Field {...radioButtonProps} />
      <label
        htmlFor={id}
        className={css.label}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        role="radio"
        aria-checked={rest.checked}
        aria-labelledby={`${id}-label`}
      >
        <div className={css.labelWrapper}>
          <span className={css.radioButtonWrapper}>
            <IconRadioButton
              className={svgClassName}
              checkedClassName={checkedClassName}
              showAsRequired={showAsRequired}
            />
          </span>
          <div id={`${id}-label`} className={css.text}>
            {label}
          </div>
        </div>
        {subLabel && <div className={`${css.text} ${css.subLabel}`}>{subLabel}</div>}
        {children && <div className={css.slot}>{children}</div>}
      </label>
    </span>
  );
};

FieldRadioButtonComponent.defaultProps = {
  className: null,
  rootClassName: null,
  svgClassName: null,
  checkedClassName: null,
  label: null,
  subLabel: null,
  children: null,
};

FieldRadioButtonComponent.propTypes = {
  className: string,
  rootClassName: string,
  svgClassName: string,
  checkedClassName: string,

  // Id is needed to connect the label with input.
  id: string.isRequired,
  label: node,
  subLabel: node,
  children: node,

  // Name groups several RadioButtones to an array of selected values
  name: string.isRequired,

  // RadioButton needs a value that is passed forward when user checks the RadioButton
  value: string.isRequired,
};

export default FieldRadioButtonComponent;
